import General from '../../src/utils/GeneralUtils';

interface TimestampDisplayProps {
  /**
   * ISO 8601 timestamp string
   */
  timestamp: string | null | undefined;

  /**
   * Which format to show first (larger)
   * - 'relative': Show "2 days ago" as primary text (default)
   * - 'absolute': Show "1/15/2025" as primary text
   */
  primaryFormat?: 'relative' | 'absolute';

  /**
   * Format for absolute date
   * - 'date': "1/15/2025" (default)
   * - 'datetime': "1/15/2025 14:30:45"
   * - 'datetime-compact': "1/15/25 14:30"
   */
  absoluteFormat?: 'date' | 'datetime' | 'datetime-compact';

  /**
   * Format for relative date
   * - 'standard': "2 days ago" (default)
   * - 'detailed': "2 hrs 15 mins ago"
   */
  relativeFormat?: 'standard' | 'detailed';

  /**
   * Show only one format (no secondary text)
   */
  singleFormat?: boolean;

  /**
   * Custom fallback text when timestamp is null/undefined
   */
  fallback?: string | React.ReactNode;
}

/**
 * TimestampDisplay - Consistent timestamp formatting component.
 *
 * Displays timestamps with both absolute and relative formats.
 * Primary format is shown in normal text, secondary in smaller desc-3 style.
 *
 * @example
 * // Default: relative primary, absolute secondary
 * <TimestampDisplay timestamp={user.created_at} />
 * // Output: "2 days ago (1/15/2025)"
 *
 * @example
 * // Absolute primary, relative secondary
 * <TimestampDisplay timestamp={user.created_at} primaryFormat="absolute" />
 * // Output: "1/15/2025 (2 days ago)"
 *
 * @example
 * // Single format only
 * <TimestampDisplay timestamp={user.created_at} singleFormat />
 * // Output: "2 days ago"
 *
 * @example
 * // Detailed relative format
 * <TimestampDisplay
 *   timestamp={log.timestamp}
 *   relativeFormat="detailed"
 * />
 * // Output: "2 hrs 15 mins ago (1/15/2025 14:30)"
 */
export function TimestampDisplay({
  timestamp,
  primaryFormat = 'relative',
  absoluteFormat = 'date',
  relativeFormat = 'standard',
  singleFormat = false,
  fallback = '-',
}: TimestampDisplayProps) {
  // Handle null/undefined timestamps
  if (!timestamp) {
    return <>{fallback}</>;
  }

  // Get formatted values
  const absoluteText = getAbsoluteText(timestamp, absoluteFormat);
  const relativeText = getRelativeText(timestamp, relativeFormat);

  // Single format mode
  if (singleFormat) {
    return <>{primaryFormat === 'relative' ? relativeText : absoluteText}</>;
  }

  // Dual format mode
  const primaryText = primaryFormat === 'relative' ? relativeText : absoluteText;
  const secondaryText = primaryFormat === 'relative' ? absoluteText : relativeText;

  return (
    <>
      {primaryText} <span className="desc-2">({secondaryText})</span>
    </>
  );
}

/**
 * Get absolute date text
 */
function getAbsoluteText(timestamp: string, format: 'date' | 'datetime' | 'datetime-compact'): string {
  switch (format) {
    case 'datetime':
      return General.formatDateTime(timestamp);
    case 'datetime-compact':
      return General.formatDateTimeCompact(timestamp);
    case 'date':
    default:
      return General.formatDate(timestamp);
  }
}

/**
 * Get relative date text
 */
function getRelativeText(timestamp: string, format: 'standard' | 'detailed'): string {
  switch (format) {
    case 'detailed':
      return General.daysAgoDetailed(timestamp) || '-';
    case 'standard':
    default:
      return General.daysAgo(timestamp) || '-';
  }
}
